export interface BrowseViewportSnapshot {
  anchorId: string | null
  anchorTop: number
  scrollY: number
}

interface BrowseViewportWindow {
  scrollTo: (x: number, y: number) => void
  scrollY: number
}

type FrameScheduler = (callback: FrameRequestCallback) => number

export type BrowseViewportScopeAction = 'preserve' | 'reset' | 'restore'

/** Decides whether a settled Browse result-scope change owns the viewport. */
export function browseViewportActionForScopeChange(
  previousScopeKey: string | null,
  nextScopeKey: string,
  restorePending: boolean,
): BrowseViewportScopeAction {
  if (restorePending) return 'restore'
  if (previousScopeKey === null || previousScopeKey === nextScopeKey) return 'preserve'
  return 'reset'
}

function visibleSearchResult(document: Document): HTMLElement | null {
  return [...document.querySelectorAll<HTMLElement>('.search-result')]
    .find((result) => result.getBoundingClientRect().bottom > 0) ?? null
}

export function captureBrowseViewport(
  document: Document = window.document,
  browserWindow: BrowseViewportWindow = window,
): BrowseViewportSnapshot {
  const anchor = visibleSearchResult(document)
  return {
    anchorId: anchor?.id || null,
    anchorTop: anchor?.getBoundingClientRect().top ?? 0,
    scrollY: browserWindow.scrollY,
  }
}

/** Restores a card's viewport position after its surrounding list has changed. */
export function restoreBrowseViewport(
  snapshot: BrowseViewportSnapshot,
  document: Document = window.document,
  browserWindow: BrowseViewportWindow = window,
): void {
  browserWindow.scrollTo(0, snapshot.scrollY)
  const anchor = snapshot.anchorId ? document.getElementById(snapshot.anchorId) : null
  if (!anchor) return
  const offset = anchor.getBoundingClientRect().top - snapshot.anchorTop
  if (Math.abs(offset) > 0.5) browserWindow.scrollTo(0, Math.max(0, browserWindow.scrollY + offset))
}

/** Retries restoration while a fixed overlay's dismissed layout rebuilds the browse document. */
export function restoreBrowseViewportWhenReady(
  snapshot: BrowseViewportSnapshot,
  frameBudget: number,
  document: Document = window.document,
  browserWindow: BrowseViewportWindow = window,
  requestFrame: FrameScheduler = window.requestAnimationFrame.bind(window),
): void {
  let frames = 0
  const restore = () => {
    frames += 1
    restoreBrowseViewport(snapshot, document, browserWindow)
    const anchor = snapshot.anchorId ? document.getElementById(snapshot.anchorId) : null
    const restored = anchor
      ? Math.abs(anchor.getBoundingClientRect().top - snapshot.anchorTop) <= 0.5
      : Math.abs(browserWindow.scrollY - snapshot.scrollY) <= 0.5
    if (!restored && frames < frameBudget) requestFrame(restore)
  }
  requestFrame(restore)
}
